import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {Access} from './types'
import {HttpGetSignatureSheetsResponse} from './http/accesses.http'

/**
 * Discarded accesses don't count towards the sheet's overall validation state:
 * a sheet is "validated" once every non-discarded access is validated, and
 * stays "to_validate" as long as any non-discarded access still needs it.
 * Any other mix (accounted, excluded, ...) is left as-is.
 */
const computeSignatureSheetGlobalStatus = (
    accesses: HttpGetSignatureSheetsResponse[0]['accesses'],
    currentGlobalStatus: HttpGetSignatureSheetsResponse[0]['globalStatus']
): HttpGetSignatureSheetsResponse[0]['globalStatus'] => {
    const relevantAccesses = accesses.filter(access => access.status !== 'discarded')
    if (relevantAccesses.length > 0 && relevantAccesses.every(access => access.status === 'validated')) {
        return 'validated'
    }
    if (relevantAccesses.some(access => access.status === 'to_validate')) {
        return 'to_validate'
    }
    return currentGlobalStatus
}

/**
 * Patches an access's status directly inside every cached signature-sheets
 * query, without invalidating/refetching. The signature-sheets endpoint may
 * exclude discarded accesses from its response — a discarded/restored access
 * must stay visible in its origin fogli firma sheet, so we update it in
 * place instead of letting a refetch potentially drop it from the list.
 */
export const updateAccessStatusInSignatureSheetsCache = (
    queryClient: ReturnType<typeof useQueryClient>,
    accessId: string,
    status: Access['status']
) => {
    queryClient.setQueriesData<HttpGetSignatureSheetsResponse>(
        {queryKey: [QUERY_KEYS.ACCESSES, QUERY_KEYS.ACCESSES_SIGNATURE_SHEETS]},
        oldData => {
            if (!oldData) return oldData
            return oldData.map(sheet => {
                if (!sheet.accesses.some(access => access.id === accessId)) return sheet
                const accesses = sheet.accesses.map(access => (access.id === accessId ? {...access, status} : access))
                return {
                    ...sheet,
                    accesses,
                    globalStatus: computeSignatureSheetGlobalStatus(accesses, sheet.globalStatus)
                }
            })
        }
    )
}

/**
 * Invalidates the accesses table query only, leaving any cached
 * signature-sheets query untouched (it shares the ACCESSES prefix, so a
 * plain invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]}) would also
 * refetch it).
 */
export const invalidateAccessesTableOnly = (queryClient: ReturnType<typeof useQueryClient>) => {
    queryClient.invalidateQueries({
        predicate: query =>
            query.queryKey[0] === QUERY_KEYS.ACCESSES && query.queryKey[1] !== QUERY_KEYS.ACCESSES_SIGNATURE_SHEETS
    })
}
